package org.zest.insan_360_website.pages.user.credits_layout

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.forms.Input
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.zest.insan_360_website.components.layouts.*
import org.zest.insan_360_website.components.widgets.*
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.AuthState
import org.zest.insan_360_website.data.page_states.dashboard.CreditsDashboardState
import org.zest.insan_360_website.data.page_states.dashboard.CreditsDashboardState.fetchSupAdmPaymentsList
import org.zest.insan_360_website.data.page_states.dashboard.CreditsDashboardState.paymentDashStats
import org.zest.insan_360_website.model.PaymentRecord
import org.zest.insan_360_website.model.PaymentStatus
import org.zest.insan_360_website.model.UserRole
import org.zest.insan_360_website.utils.convertToIsoDate
import org.zest.insan_360_website.utils.formatToReadableDateJS

@OptIn(DelicateApi::class)
@Composable
fun SuperAdminLayout() {
    val scope = rememberCoroutineScope()
    LaunchedEffect(
        CreditsDashboardState.supAdmStartDate,
        CreditsDashboardState.supAdmEndDate,
        CreditsDashboardState.supAdmPaymentFilter,
        CreditsDashboardState.supAdmCurrentPage
    ) {
        val isoStart = convertToIsoDate(CreditsDashboardState.supAdmStartDate)
        val isoEnd = convertToIsoDate(CreditsDashboardState.supAdmEndDate, isEnd = true)
        val mappedStatus =
            if (CreditsDashboardState.supAdmPaymentFilter == "BOTH") null else CreditsDashboardState.supAdmPaymentFilter
        fetchSupAdmPaymentsList(scope, isoStart, isoEnd, mappedStatus, CreditsDashboardState.supAdmCurrentPage)
    }

    PageContentBaseCol {
        val breakpoint = rememberBreakpoint()
        val stats = paymentDashStats

        if (breakpoint < Breakpoint.MD) {
            Column(Modifier.fillMaxWidth().gap(1.cssRem).padding(bottom = 1.cssRem)) {
                SectionTitle(userDashWordLibrary.system_credit_purchase_record.localized)
                DateFilterControl(
                    startDate = CreditsDashboardState.supAdmStartDate,
                    endDate = CreditsDashboardState.supAdmEndDate,
                    onStartDateChange = { CreditsDashboardState.handleSupAdmStartDateChange(it) },
                    onEndDateChange = { CreditsDashboardState.handleSupAdmEndDateChange(it) }
                )
                PaymentStatusFilterTabs(CreditsDashboardState.supAdmPaymentFilter) {
                    CreditsDashboardState.supAdmPaymentFilter = it; CreditsDashboardState.supAdmCurrentPage = 1
                }
                if (stats != null && stats.totalPages > 1) {
                    PaginationControl(
                        totalPages = stats.totalPages,
                        currentPage = CreditsDashboardState.supAdmCurrentPage,
                        breakpoint = breakpoint,
                        onPageChange = { CreditsDashboardState.supAdmCurrentPage = it }
                    )
                }
            }
        }

        PageContentContainer(
            Modifier
                .padding(0.px)
                .border { width(0.px) }
                .flex(1)
        ) {
            if (breakpoint >= Breakpoint.MD) {
                FlushedListContainer(header = {
                    Row(
                        Modifier.fillMaxWidth().justifyContent(JustifyContent.SpaceBetween),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SectionTitle(userDashWordLibrary.system_credit_purchase_record.localized)
                        Row(Modifier.gap(1.cssRem), verticalAlignment = Alignment.CenterVertically) {
                            PaymentStatusFilterTabs(CreditsDashboardState.supAdmPaymentFilter) {
                                CreditsDashboardState.supAdmPaymentFilter =
                                    it; CreditsDashboardState.supAdmCurrentPage = 1
                            }
                            DateFilterControl(
                                startDate = CreditsDashboardState.supAdmStartDate,
                                endDate = CreditsDashboardState.supAdmEndDate,
                                onStartDateChange = { CreditsDashboardState.handleSupAdmStartDateChange(it) },
                                onEndDateChange = { CreditsDashboardState.handleSupAdmEndDateChange(it) }
                            )
                        }
                    }
                }) {
                    stats?.payments?.forEachIndexed { index, transaction ->
                        if (breakpoint >= Breakpoint.LG) SuperAdminPaymentRowLG(index, transaction)
                        else SuperAdminPaymentRow(index, transaction)
                    }
                }
            } else {
                FlushedListContainer {
                    stats?.payments?.forEachIndexed { index, transaction ->
                        SuperAdminPaymentRow(index, transaction)
                    }
                }
            }
        }

        if (breakpoint >= Breakpoint.MD && stats != null && stats.totalPages > 1) {
            PaginationControl(
                totalPages = stats.totalPages,
                currentPage = CreditsDashboardState.supAdmCurrentPage,
                breakpoint = breakpoint,
                onPageChange = { CreditsDashboardState.supAdmCurrentPage = it }
            )
        }
    }
}

@OptIn(DelicateApi::class)
@Composable
fun DateFilterControl(
    startDate: String?,
    endDate: String?,
    onStartDateChange: (String) -> Unit,
    onEndDateChange: (String) -> Unit
) {
    val isMobile = rememberBreakpoint() < Breakpoint.MD
    Row(
        Modifier.gap(0.5.cssRem)
            .then(if (isMobile) Modifier.fillMaxWidth().justifyContent(JustifyContent.Center) else Modifier),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(userDashWordLibrary.from.localized, Modifier.fontSize(0.875.cssRem).fontWeight(FontWeight.Medium))
        Input(
            type = InputType.Date,
            value = startDate ?: "",
            onValueChange = onStartDateChange,
            modifier = baseInputStyle.toModifier()
                .padding(topBottom = 0.25.cssRem, leftRight = 0.5.cssRem)
                .fontSize(0.875.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .borderRadius(0.5.cssRem)
                .then(if (isMobile) Modifier.flex(1) else Modifier)
        )
        SpanText(userDashWordLibrary.to.localized, Modifier.fontSize(0.875.cssRem).fontWeight(FontWeight.Medium))
        Input(
            type = InputType.Date,
            value = endDate ?: "",
            onValueChange = onEndDateChange,
            modifier = baseInputStyle.toModifier()
                .padding(topBottom = 0.25.cssRem, leftRight = 0.5.cssRem)
                .fontSize(0.875.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .borderRadius(0.5.cssRem)
                .then(if (isMobile) Modifier.flex(1) else Modifier)
        )
    }
}

@Composable
fun PaginationControl(
    totalPages: Int,
    currentPage: Int,
    breakpoint: Breakpoint,
    onPageChange: (Int) -> Unit
) {
    Row(
        Modifier.fillMaxWidth().padding(top = 1.cssRem).justifyContent(
            if (breakpoint >= Breakpoint.MD) JustifyContent.End else JustifyContent.Center
        ).gap(0.25.cssRem),
        verticalAlignment = Alignment.CenterVertically
    ) {
        val pagesToShow = mutableListOf<String>()
        if (totalPages <= 5) {
            for (i in 1..totalPages) pagesToShow.add(i.toString())
        } else {
            if (currentPage <= 3) {
                pagesToShow.add("1"); pagesToShow.add("2"); pagesToShow.add("3")
                pagesToShow.add("...")
                pagesToShow.add(totalPages.toString())
            } else if (currentPage >= totalPages - 2) {
                pagesToShow.add("1")
                pagesToShow.add("...")
                pagesToShow.add((totalPages - 2).toString())
                pagesToShow.add((totalPages - 1).toString())
                pagesToShow.add(totalPages.toString())
            } else {
                pagesToShow.add("1")
                pagesToShow.add("...")
                pagesToShow.add(currentPage.toString())
                pagesToShow.add("...")
                pagesToShow.add(totalPages.toString())
            }
        }

        pagesToShow.forEach { pageStr ->
            if (pageStr == "...") {
                SpanText("...", Modifier.padding(leftRight = 0.5.cssRem))
            } else {
                val pNum = pageStr.toInt()
                val isSelected = (pNum == currentPage)
                Box(
                    Modifier
                        .minWidth(2.cssRem)
                        .height(2.cssRem)
                        .borderRadius(0.25.cssRem)
                        .backgroundColor(if (isSelected) userPagePrimaryTextColor else Colors.Transparent)
                        .color(if (isSelected) userPageBGColor else userPagePrimaryTextColor)
                        .border {
                            if (!isSelected) {
                                width(1.px)
                                style(LineStyle.Solid)
                                color(userPageBorderStrongColor)
                            }
                        }
                        .cursor(Cursor.Pointer)
                        .onClick { if (!isSelected) onPageChange(pNum) },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(pageStr, Modifier.fontSize(0.875.cssRem).fontWeight(FontWeight.Bold))
                }
            }
        }
    }
}

@OptIn(DelicateApi::class)
@Composable
fun PaymentStatusFilterTabs(
    currentFilter: String,
    onFilterSelect: (String) -> Unit
) {
    val isMobile = rememberBreakpoint() < Breakpoint.MD
    Row(
        Modifier
            .backgroundColor(userPageBorderStrongColor.copyf(alpha = 0.5f))
            .borderRadius(0.25.cssRem)
            .padding(0.25.cssRem)
            .gap(0.25.cssRem)
            .alignItems(AlignItems.Center)
            .then(if (isMobile) Modifier.fillMaxWidth().justifyContent(JustifyContent.Center) else Modifier)
    ) {
        listOf("PAID", "FAILED").forEach { filter ->
            val isSelected = filter == currentFilter
            Box(
                Modifier
                    .padding(leftRight = 0.75.cssRem, topBottom = 0.3.cssRem)
                    .borderRadius(0.2.cssRem)
                    .cursor(Cursor.Pointer)
                    .backgroundColor(if (isSelected) userPageColor else Colors.Transparent)
                    .then(if (isMobile) Modifier.flex(1) else Modifier)
                    .onClick {
                        if (isSelected) onFilterSelect("BOTH")
                        else onFilterSelect(filter)
                    },
                contentAlignment = Alignment.Center
            ) {
                val statusLabel = userDashWordLibrary.paymentStatus[PaymentStatus.valueOf(filter)]?.localized ?: filter
                val displayLabel =
                    statusLabel.lowercase().replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
                SpanText(displayLabel, Modifier.fontSize(0.85.cssRem).fontWeight(FontWeight.Medium))
            }
        }
    }
}

@Composable
fun SuperAdminPaymentRowLG(index: Int, transaction: PaymentRecord) {
    FlushedListRowWrapper(index = index) {
        Row(Modifier.fillMaxWidth().gap(0.25.cssRem), verticalAlignment = Alignment.CenterVertically) {
            Column(Modifier.flex(2), verticalArrangement = Arrangement.Center) {
                CopyableIdText(transaction.id)
                TransactionLabel(userDashWordLibrary.id_transaction.localized)
            }
            Column(Modifier.flex(2), verticalArrangement = Arrangement.Center) {
                CopyableIdText(transaction.toyyibpayId)
                TransactionLabel(userDashWordLibrary.id_toyyib.localized)
            }
            Column(Modifier.flex(5).minWidth(0.px), verticalArrangement = Arrangement.Center) {
                if (transaction.organizationName.isNullOrEmpty()) {
                    SpanText(
                        userDashWordLibrary.no_affiliation.localized,
                        Modifier.fontSize(0.875.cssRem).fontWeight(FontWeight.Medium)
                    )
                } else {
                    SpanText(
                        transaction.organizationName,
                        Modifier
                            .fillMaxWidth()
                            .fontSize(0.875.cssRem)
                            .fontWeight(FontWeight.Medium)
                            .whiteSpace(WhiteSpace.NoWrap)
                            .overflow(Overflow.Hidden)
                            .textOverflow(TextOverflow.Ellipsis)
                            .title(transaction.organizationName)
                    )
                }
                TransactionLabel(userDashWordLibrary.organization.localized)
            }
            Box(Modifier.flex(2).fillMaxSize(), contentAlignment = Alignment.CenterStart) {
                TransactionStatusBadge(transaction.status)
            }
            Column(
                Modifier.flex(1),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.End
            ) {
                SpanText(
                    transaction.purchasedAmount.toString(),
                    Modifier.fontSize(0.875.cssRem),
                )
                TransactionLabel(userDashWordLibrary.credit.localized, TextAlign.End)
            }
            Column(
                Modifier.flex(2),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.End
            ) {
                TransactionAmountDisplay(transaction.amountPaidCents)
                TransactionLabel(userDashWordLibrary.total.localized, TextAlign.End)
            }
            Column(
                Modifier.flex(4),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.End
            ) {
                SpanText(
                    formatToReadableDateJS(transaction.purchaseAt),
                    Modifier.fontSize(0.875.cssRem).textAlign(TextAlign.End)
                )
                TransactionLabel(userDashWordLibrary.time_transaction.localized, TextAlign.End)
            }
        }
    }
}

@Composable
fun SuperAdminPaymentRow(index: Int, transaction: PaymentRecord) {
    FlushedListRowWrapper(gap = 0.25.cssRem, index = index, Modifier.gap(1.cssRem)) {
        Row(
            Modifier
                .fillMaxWidth()
                .justifyContent(JustifyContent.SpaceBetween)
                .gap(0.5.cssRem)
        ) {
            Column(Modifier.flex(2).padding(right = 0.5.cssRem), verticalArrangement = Arrangement.Center) {
                CopyableIdText(transaction.id)
                TransactionLabel(userDashWordLibrary.id_transaction.localized)
            }
            Column(
                Modifier.flex(4).padding(right = 0.5.cssRem).minWidth(0.px),
                verticalArrangement = Arrangement.Center
            ) {
                if (transaction.organizationName.isNullOrEmpty()) {
                    SpanText("No Affiliation", Modifier.fontSize(0.875.cssRem).fontWeight(FontWeight.Medium))
                } else {
                    SpanText(
                        transaction.organizationName,
                        Modifier
                            .fillMaxWidth()
                            .fontSize(0.875.cssRem)
                            .fontWeight(FontWeight.Medium)
                            .whiteSpace(WhiteSpace.NoWrap)
                            .overflow(Overflow.Hidden)
                            .textOverflow(TextOverflow.Ellipsis)
                            .title(transaction.organizationName)
                    )
                }
                TransactionLabel(userDashWordLibrary.organization.localized)
            }
            Column(
                Modifier.flex(1),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.End
            ) {
                TransactionStatusBadge(transaction.status)
            }
        }

        Row(Modifier.fillMaxWidth()) {
            Column(
                Modifier.fillMaxWidth().padding(right = 0.5.cssRem),
                verticalArrangement = Arrangement.Center
            ) {
                CopyableIdText(transaction.toyyibpayId)
                TransactionLabel(userDashWordLibrary.id_toyyib.localized)
            }
            Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.Center) {
                TransactionAmountDisplay(transaction.amountPaidCents)
                TransactionLabel(userDashWordLibrary.total.localized)
            }
            Box(Modifier.fillMaxWidth().flex(1))
        }

        Row(Modifier.fillMaxWidth()) {
            Column(
                Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.Center
            ) {
                SpanText(
                    formatToReadableDateJS(transaction.purchaseAt),
                    Modifier.fontSize(0.875.cssRem)
                )
                TransactionLabel(userDashWordLibrary.time_transaction.localized)
            }
            Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.Center) {
                SpanText(
                    transaction.purchasedAmount.toString(),
                    Modifier.fontSize(0.875.cssRem)
                )
                TransactionLabel(userDashWordLibrary.credit.localized)
            }
        }
    }
}

@Composable
private fun TransactionLabel(text: String, textAlign: TextAlign = TextAlign.Start) {
    SpanText(
        text,
        Modifier
            .fontSize(0.875.cssRem)
            .fontWeight(FontWeight.Medium)
            .color(userPageSecondaryTextColor)
            .textAlign(textAlign)
    )
}

@Composable
private fun TransactionStatusBadge(status: PaymentStatus) {
    val displayStatus = if (status == PaymentStatus.PENDING) PaymentStatus.FAILED else status

    val statusColor = when (displayStatus) {
        PaymentStatus.PAID -> Color.rgb(0x16a34a)
        else -> Color.rgb(0xdc2626)
    }

    StatusBadge(
        text = userDashWordLibrary.paymentStatus[displayStatus]?.localized ?: when (displayStatus) {
            PaymentStatus.PAID -> "PAID"
            else -> "FAILED"
        },
        color = statusColor
    )
}

@Composable
private fun TransactionAmountDisplay(cents: Int) {
    val dollars = cents / 100
    val remainder = cents % 100
    val formattedAmount = "$dollars.${remainder.toString().padStart(2, '0')}"
    Row(Modifier.gap(0.125.cssRem), verticalAlignment = Alignment.Bottom) {
        SpanText(
            "RM",
            Modifier
                .fontSize(0.7.cssRem)
                .color(userPageSecondaryTextColor)
        )
        SpanText(formattedAmount, Modifier.fontSize(0.875.cssRem))
    }
}
